import { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getTenantInfo } from '@/utils/tenant';
import { useOrgModuleAccess } from '@/hooks/useOrgModuleAccess';
import { useGetWorkspaceContextQuery } from '@/store/api/apiSlice';
import { isWorkspaceRouterEnabled } from '@/utils/postLoginNavigation';
import { useProjectContext } from '@/context/ProjectContext';
import { getActiveProductId, type TenantProductsMap } from '@/utils/productAccess';
import { PRODUCT_BY_ID, type ProductId } from '@/config/products';

export type PlatformContextValue = {
  activeProductId: ProductId;
  activeProductName: string;
  planTemplateId?: string;
  entitled: ProductId[];
  productAccess: TenantProductsMap;
  isEntitled: (productId: ProductId) => boolean;
  selectedProjectId: string | null;
  workspaceRouterEnabled: boolean;
};

const PlatformContext = createContext<PlatformContextValue | null>(null);

export function PlatformProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { user } = useAuth();
  const tenantSettings = (user?.tenant?.settings ?? getTenantInfo()?.settings) as
    | Record<string, unknown>
    | undefined;
  const workspaceRouterEnabled = isWorkspaceRouterEnabled();
  const { productAccess, entitled, isEntitled, planTemplateId } = useOrgModuleAccess(tenantSettings);
  const { selectedProjectId } = useProjectContext();
  useGetWorkspaceContextQuery(undefined, { skip: !workspaceRouterEnabled });

  const activeProductId = getActiveProductId(location.pathname, location.search);

  const value = useMemo(
    (): PlatformContextValue => ({
      activeProductId,
      activeProductName: PRODUCT_BY_ID[activeProductId].shortName,
      planTemplateId,
      entitled: entitled ?? [],
      productAccess,
      isEntitled,
      selectedProjectId,
      workspaceRouterEnabled,
    }),
    [
      activeProductId,
      planTemplateId,
      entitled,
      productAccess,
      isEntitled,
      selectedProjectId,
      workspaceRouterEnabled,
    ],
  );

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
}

export function usePlatformContext(): PlatformContextValue {
  const ctx = useContext(PlatformContext);
  if (!ctx) {
    throw new Error('usePlatformContext must be used within PlatformProvider');
  }
  return ctx;
}

/** Safe optional hook for components outside provider (should not happen). */
export function usePlatformContextOptional(): PlatformContextValue | null {
  return useContext(PlatformContext);
}
